import { useCallback, useMemo } from 'react';
import type { ComplianceRuleDocument } from '@store/generated/jotcoach';

import type { ComplianceRule } from '../types';

import {
  useGetJotCoachRulesQuery,
  useGetJotCoachSessionByNoteQuery,
  useLazyGetJotCoachEncounterSessionQuery,
  useLazyGetJotCoachSessionByNoteQuery,
} from './api';

/**
 * Type definitions for JotCoach session data structure
 */
export interface JotCoachGap {
  id: string;
  title: string;
  description: string;
  section_id: string | null;
  note_section: string;
  priority: 'High' | 'Medium' | 'Low';
  impact: string;
  reason: string;
  rule_anchor: string;
  evidence_from_draft: string[];
  evidence_from_transcript: string[];
  suggested_edit: {
    op: 'insert' | 'replace';
    section: string;
    section_id: string | null;
    anchor: string;
    content: string;
    confidence: number;
  };
}

export interface JotCoachProviderQuestion {
  id: string;
  question: string;
  why_needed: string;
  section_id: string | null;
  note_section: string;
  expected_format: string;
  example_response: string;
}

export interface JotCoachDocumentationAnalysis {
  gaps: JotCoachGap[];
  provider_questions: JotCoachProviderQuestion[];
  overall_quality_score: number;
  quality_summary: string;
  billing_support: {
    proposed_primary_code: string;
    supports_suggested_cpt: boolean;
    confidence: number;
    strengths: string[];
    concerns: string[];
    missing_elements_for_code: string[];
  };
  scores: {
    clinical_quality: number;
    completeness: number;
    compliance: number;
    specificity: number;
  };
  tone_for_provider: string;
}

export interface JotCoachSynthesizedFeedback {
  detailed_feedback: string;
  sms_friendly_feedback: string;
}

export interface JotCoachSessionData {
  documentation_analysis?: JotCoachDocumentationAnalysis | null;
  session_analysis?: any;
  gap_analysis?: string | null;
  message_history?: any[];
  sms_nudge?: string | null;
  synthesized_feedback?: JotCoachSynthesizedFeedback | null;
}

/**
 * Hook to get JotCoach gaps filtered by section_id
 * @param noteId - The note ID to fetch session data for
 * @param sectionId - The section ID to filter gaps by (null or undefined returns all gaps)
 * @param pollingInterval - Polling interval in milliseconds (0 = no polling)
 *
 * Controlled polling - parent component manages when to poll
 */
export function useJotCoachGapsForSection(
  noteId: string | undefined,
  sectionId?: string | null,
  pollingInterval: number = 0,
) {
  const {
    data: session,
    isLoading,
    error,
  } = useGetJotCoachSessionByNoteQuery(
    { noteId: noteId || '' },
    {
      skip: !noteId,
      pollingInterval: noteId ? pollingInterval : 0,
    },
  );

  const filteredGaps = useMemo((): JotCoachGap[] => {
    if (!session?.session_data?.documentation_analysis?.gaps) {
      return [];
    }

    const gaps = session.session_data.documentation_analysis.gaps as JotCoachGap[];

    // If no sectionId provided, return all gaps
    if (sectionId === undefined || sectionId === null) {
      return gaps;
    }

    // Filter gaps by section_id
    return gaps.filter((gap: JotCoachGap) => gap.section_id === sectionId);
  }, [session, sectionId ?? '']);

  const providerQuestions = useMemo((): JotCoachProviderQuestion[] => {
    if (!session?.session_data?.documentation_analysis?.provider_questions) {
      return [];
    }

    const questions = session.session_data.documentation_analysis
      .provider_questions as JotCoachProviderQuestion[];

    // If no sectionId provided, return all questions
    if (sectionId === undefined || sectionId === null) {
      return questions;
    }

    // Filter questions by section_id
    return questions.filter(
      (question: JotCoachProviderQuestion) => question.section_id === sectionId,
    );
  }, [session, sectionId ?? '']);

  return {
    gaps: filteredGaps,
    providerQuestions,
    isLoading,
    error,
    hasGaps: filteredGaps.length > 0,
    hasQuestions: providerQuestions.length > 0,
    session, // Include full session for convenience
  };
}

/**
 * Hook to get full JotCoach session documentation analysis using lazy query
 * This allows manual control over when to fetch/refetch, similar to encounter documents polling
 * @param noteId - The note ID to fetch session data for
 *
 * Returns:
 * - fetchSession: Function to manually trigger a fetch
 * - documentationAnalysis: The current documentation analysis data (or null)
 * - session: The full session data
 * - isLoading: Whether a fetch is currently in progress
 * - error: Any error from the last fetch
 */
export function useJotCoachDocumentationAnalysisLazy(noteId: string | undefined) {
  const [fetchSession, { data: session, isLoading, error }] =
    useLazyGetJotCoachSessionByNoteQuery();

  const documentationAnalysis = useMemo(() => {
    return session?.session_data?.documentation_analysis || null;
  }, [session]);

  // Stable fetch function that checks noteId before fetching
  const fetchJotCoachSession = useCallback(() => {
    if (noteId) {
      fetchSession({ noteId });
    }
  }, [noteId, fetchSession]);

  return {
    fetchSession: fetchJotCoachSession,
    documentationAnalysis,
    isLoading,
    error,
    session,
  };
}

/**
 * Hook to get full JotCoach session documentation analysis
 * @param noteId - The note ID to fetch session data for
 * @param pollingInterval - Polling interval in milliseconds (0 = no polling)
 *
 * Controlled polling - parent component manages when to poll
 * @deprecated Consider using useJotCoachDocumentationAnalysisLazy for better control
 */
export function useJotCoachDocumentationAnalysis(
  noteId: string | undefined,
  pollingInterval: number = 0,
) {
  const {
    data: session,
    isLoading,
    error,
  } = useGetJotCoachSessionByNoteQuery(
    { noteId: noteId || '' },
    {
      skip: !noteId,
      pollingInterval: noteId ? pollingInterval : 0,
    },
  );

  const documentationAnalysis = useMemo(() => {
    return session?.session_data?.documentation_analysis || null;
  }, [session]);

  return {
    documentationAnalysis,
    isLoading,
    error,
    session,
  };
}

/**
 * Hook to get JotCoach encounter session using lazy query
 * This allows manual control over when to fetch/refetch, similar to note session polling
 * @param encounterId - The encounter ID to fetch session data for
 *
 * Returns:
 * - fetchSession: Function to manually trigger a fetch
 * - encounterSession: The current encounter session data (or null)
 * - isLoading: Whether a fetch is currently in progress
 * - error: Any error from the last fetch
 */
export function useJotCoachEncounterSessionLazy(encounterId: string | undefined) {
  const [fetchSession, { data: encounterSession, isLoading, error }] =
    useLazyGetJotCoachEncounterSessionQuery();

  // Stable fetch function that checks encounterId before fetching
  const fetchEncounterSession = useCallback(() => {
    if (encounterId) {
      fetchSession({ encounterId });
    }
  }, [encounterId, fetchSession]);

  return {
    fetchSession: fetchEncounterSession,
    encounterSession,
    isLoading,
    error,
  };
}

/**
 * Hook to get JotCoach session analysis (CPT codes, complexity, etc.)
 * @param noteId - The note ID to fetch session data for
 */
export function useJotCoachSessionAnalysis(noteId: string | undefined) {
  const {
    data: session,
    isLoading,
    error,
  } = useGetJotCoachSessionByNoteQuery({ noteId: noteId || '' }, { skip: !noteId });

  const sessionAnalysis = useMemo(() => {
    return session?.session_data?.session_analysis || null;
  }, [session]);

  return {
    sessionAnalysis,
    isLoading,
    error,
    session,
  };
}

/**
 * Maps a ComplianceRuleDocument from the API to ComplianceRule format used by components
 */
function mapRuleDocumentToComplianceRule(rule: ComplianceRuleDocument): ComplianceRule {
  return {
    id: rule.rule_id,
    rule_text: rule.rule_text,
    criterion: rule.criterion,
    compliance_rationale: rule.compliance_rationale,
    format_or_wording_required: rule.format_or_wording_required || '',
    rule_type: rule.rule_type,
    applicable_cpt_codes: rule.applicable_cpt_codes || [],
    payer_type: rule.payer_type === 'general' ? 'general' : 'specific',
    applicable_payer_name: rule.payer_type === 'specific' ? rule.title || null : null,
    source_document: rule.source_document || 'Unknown',
    source_date: rule.source_date || '',
    other_artifacts_required: null, // API doesn't provide this field
  };
}

/**
 * Hook to get JotCoach compliance rules from the API
 * @param maxKeys - Maximum number of rules to return (optional)
 * @returns Rules mapped to ComplianceRule format, loading state, and error
 */
export function useJotCoachRules(maxKeys?: number): {
  rules: ComplianceRule[];
  isLoading: boolean;
  error: unknown;
  total: number;
} {
  const {
    data: rulesResponse,
    isLoading,
    error,
  } = useGetJotCoachRulesQuery({ maxKeys }, { skip: false });

  const rules = useMemo((): ComplianceRule[] => {
    if (!rulesResponse?.rules || rulesResponse.rules.length === 0) {
      return [];
    }

    return rulesResponse.rules.map(mapRuleDocumentToComplianceRule);
  }, [rulesResponse]);

  return {
    rules,
    isLoading,
    error,
    total: rulesResponse?.total || 0,
  };
}
